import * as React from "react";
import type { AppUser } from "../../services/userService";
import { RdPopover } from "../redesign/table/RdPopover";
import type { NoteRefArt } from "../../services/vorgangService";
import type { BezugKandidat } from "./bezugKandidaten";
import type { KommentarSenden } from "./KommentarEingabe";
import { KommentarFaden, type KommentarAendern } from "./KommentarFaden";
import type { Kommentar } from "./kommentarModell";
import "./notizbloecke.css";

/**
 * Die Sprechblase am Kopf eines Blocks oder einer Ereigniskarte
 * (PO 13./14.09.2026: „mache daraus dann wie bei microsoft word ein
 * kommentar/chat icon und wenn man draufdrueckt dann werden die kommentare
 * angezeigt. und ich kann kommentieren.").
 *
 * ══ SICHTBAR HEISST KOMMENTIERT ════════════════════════════════════════════
 * Ist etwas kommentiert, steht die Blase MIT ZAHL immer da — man soll es sehen,
 * ohne etwas zu überfahren. Ist nichts kommentiert, erscheint sie erst beim
 * Überfahren des Blocks (CSS `.nb-marke[data-leer="true"]`): eine leere Blase
 * an jedem Block wäre eine Einladung, die niemand ausgesprochen hat.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ POPOVER STATT WORD-RANDSPALTE ══════════════════════════════════════════
 * Word stellt die Kommentare in eine eigene Spalte am Blattrand. V4 hat diese
 * Spalte nicht: der Strom ist bei 620 px gedeckelt (`v4Massgaben.test.ts`) und
 * die Seite reserviert rechts nichts. Der Faden öffnet deshalb als Popover
 * NEBEN der Blase — dieselbe Stelle, dieselbe Lesart, ohne das Raster zu
 * brechen. `RdPopover` bringt Portal, Viewport-Klemmung, Escape und
 * Fokusführung mit; die Randspalte bliebe ein eigenes Optik-Paket mit
 * PO-Abnahme.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Das Motiv. Bewusst HIER und nicht aus `vorgang/v3/vg3Icons` importiert:
 * `components/notizbloecke/` trägt auch das Kundentagebuch und kennt bisher
 * keine Vorgangsansicht — ein Import von dort zöge V3 in jede Fläche, die
 * Blöcke zeichnet. Machart und Maße sind dieselben (20er-Viewbox, 1,7 px
 * Strich, `currentColor`).
 */
const Sprechblase: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M17 11.2a2.3 2.3 0 0 1-2.3 2.3H7.6L4 16.5v-3h-.7a2.3 2.3 0 0 1-2.3-2.3V5.8A2.3 2.3 0 0 1 3.3 3.5h11.4A2.3 2.3 0 0 1 17 5.8z" />
    </svg>
);

export const KommentarMarke: React.FC<{
    kommentare: Kommentar[];
    users: AppUser[];
    /** Worauf sich der Faden bezieht — Kopfzeile im Popover. */
    titel: string;
    /** Fehlt er, ist der Faden nur zu lesen (kein Schreibrecht am Vorgang). */
    onNeu?: KommentarSenden;
    /** Wer liest — entscheidet, an welchen Beiträgen „Ändern"/„Löschen" steht. */
    aktuellerUserId?: string;
    onAendern?: KommentarAendern;
    onLoeschen?: (k: Kommentar) => Promise<void>;
    /** Der Haken (PO 28.09.2026: „Kommentare kann man erledigen"). */
    onErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    kandidaten?: BezugKandidat[];
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    mitAnhang?: boolean;
}> = ({
    kommentare,
    users,
    titel,
    onNeu,
    aktuellerUserId,
    onAendern,
    onLoeschen,
    onErledigt,
    kandidaten,
    onBezugOeffnen,
    mitAnhang,
}) => {
    const anzahl = kommentare.length;
    // Ohne Kommentare UND ohne Schreibrecht gäbe es nichts zu zeigen und nichts
    // zu tun — dann bleibt die Blase ganz weg.
    if (anzahl === 0 && !onNeu) return null;

    const beschriftung =
        anzahl === 0
            ? "Kommentieren"
            : `Kommentare (${anzahl})`;

    return (
        <RdPopover
            align="end"
            width={340}
            dialogLabel={beschriftung}
            trigger={({ open, toggle, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    className="nb-marke"
                    data-leer={anzahl === 0 ? "true" : "false"}
                    aria-expanded={open}
                    aria-label={beschriftung}
                    title={beschriftung}
                    // Der Klick darf den Cursor nicht aus dem Block reißen und
                    // dort kein Blur-Speichern auslösen — derselbe Griff wie an
                    // den Knöpfen des Rich-Text-Editors.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggle();
                    }}
                >
                    <Sprechblase />
                    {anzahl > 0 ? <span className="nb-marke-n">{anzahl}</span> : null}
                </button>
            )}
        >
            {() => (
                <KommentarFaden
                    kommentare={kommentare}
                    users={users}
                    titel={titel}
                    {...(onNeu ? { onNeu } : {})}
                    {...(aktuellerUserId ? { aktuellerUserId } : {})}
                    {...(onAendern ? { onAendern } : {})}
                    {...(onLoeschen ? { onLoeschen } : {})}
                    {...(onErledigt ? { onErledigt } : {})}
                    {...(kandidaten ? { kandidaten } : {})}
                    {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                    {...(mitAnhang ? { mitAnhang } : {})}
                />
            )}
        </RdPopover>
    );
};
